import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  Menu,
  MenuItem,
  IconButton,
  TextField,
  Divider,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import ScheduleIcon from '@mui/icons-material/Schedule';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import ClearIcon from '@mui/icons-material/Clear';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { usePiers } from '../context/PiersContext';
import { computeBeltGroups } from '../utils/beltUtils';

// Custom TimePicker Component
const TimePicker = ({ value, onChange, label }) => {
  // Convert 24-hour time to 12-hour format with AM/PM
  const parse24Hour = (time24) => {
    if (!time24) return { hours: 12, minutes: 0, period: 'AM' };
    const [h, m] = time24.split(':').map(Number);
    const hours = h === 0 ? 12 : h > 12 ? h - 12 : h;
    const minutes = m;
    const period = h >= 12 ? 'PM' : 'AM';
    return { hours, minutes, period };
  };

  // Convert 12-hour format to 24-hour time
  const format24Hour = (hours, minutes, period) => {
    let h = hours;
    if (period === 'AM' && h === 12) h = 0;
    if (period === 'PM' && h !== 12) h += 12;
    return `${h.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  const { hours, minutes, period } = parse24Hour(value);

  const incrementHours = () => {
    const newHours = hours === 12 ? 1 : hours + 1;
    onChange(format24Hour(newHours, minutes, period));
  };

  const decrementHours = () => {
    const newHours = hours === 1 ? 12 : hours - 1;
    onChange(format24Hour(newHours, minutes, period));
  };

  const incrementMinutes = () => {
    const newMinutes = minutes === 59 ? 0 : minutes + 1;
    onChange(format24Hour(hours, newMinutes, period));
  };

  const decrementMinutes = () => {
    const newMinutes = minutes === 0 ? 59 : minutes - 1;
    onChange(format24Hour(hours, newMinutes, period));
  };

  const togglePeriod = () => {
    const newPeriod = period === 'AM' ? 'PM' : 'AM';
    onChange(format24Hour(hours, minutes, newPeriod));
  };

  return (
    <Box>
      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.65rem', mb: 0.25, display: 'block' }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', gap: 0.3, alignItems: 'center' }}>
        {/* Hours */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2, alignItems: 'center' }}>
          <IconButton
            size="small"
            onClick={incrementHours}
            sx={{
              color: '#F9B612',
              bgcolor: 'rgba(0, 0, 0, 0.2)',
              height: 18,
              width: 18,
              minWidth: 18,
              padding: 0,
              '&:hover': { bgcolor: 'rgba(249, 182, 18, 0.2)' }
            }}
          >
            <AddIcon sx={{ fontSize: 12 }} />
          </IconButton>
          <Box
            sx={{
              bgcolor: 'rgba(0, 0, 0, 0.3)',
              color: '#fff',
              px: 0.5,
              py: 0.25,
              borderRadius: 0.5,
              minWidth: 26,
              textAlign: 'center',
              fontSize: '0.75rem',
              fontWeight: 600,
              border: '1px solid rgba(255, 255, 255, 0.1)',
              lineHeight: 1.2
            }}
          >
            {hours.toString().padStart(2, '0')}
          </Box>
          <IconButton
            size="small"
            onClick={decrementHours}
            sx={{
              color: '#F9B612',
              bgcolor: 'rgba(0, 0, 0, 0.2)',
              height: 18,
              width: 18,
              minWidth: 18,
              padding: 0,
              '&:hover': { bgcolor: 'rgba(249, 182, 18, 0.2)' }
            }}
          >
            <RemoveIcon sx={{ fontSize: 12 }} />
          </IconButton>
        </Box>

        <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: '0.85rem', mx: 0 }}>:</Typography>

        {/* Minutes */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2, alignItems: 'center' }}>
          <IconButton
            size="small"
            onClick={incrementMinutes}
            sx={{
              color: '#F9B612',
              bgcolor: 'rgba(0, 0, 0, 0.2)',
              height: 18,
              width: 18,
              minWidth: 18,
              padding: 0,
              '&:hover': { bgcolor: 'rgba(249, 182, 18, 0.2)' }
            }}
          >
            <AddIcon sx={{ fontSize: 12 }} />
          </IconButton>
          <Box
            sx={{
              bgcolor: 'rgba(0, 0, 0, 0.3)',
              color: '#fff',
              px: 0.5,
              py: 0.25,
              borderRadius: 0.5,
              minWidth: 26,
              textAlign: 'center',
              fontSize: '0.75rem',
              fontWeight: 600,
              border: '1px solid rgba(255, 255, 255, 0.1)',
              lineHeight: 1.2
            }}
          >
            {minutes.toString().padStart(2, '0')}
          </Box>
          <IconButton
            size="small"
            onClick={decrementMinutes}
            sx={{
              color: '#F9B612',
              bgcolor: 'rgba(0, 0, 0, 0.2)',
              height: 18,
              width: 18,
              minWidth: 18,
              padding: 0,
              '&:hover': { bgcolor: 'rgba(249, 182, 18, 0.2)' }
            }}
          >
            <RemoveIcon sx={{ fontSize: 12 }} />
          </IconButton>
        </Box>

        {/* AM/PM Toggle */}
        <Box
          onClick={togglePeriod}
          sx={{
            ml: 0.3,
            bgcolor: 'rgba(0, 0, 0, 0.3)',
            color: '#fff',
            px: 0.5,
            py: 0.25,
            borderRadius: 0.5,
            minWidth: 28,
            textAlign: 'center',
            fontSize: '0.7rem',
            fontWeight: 600,
            border: '1px solid',
            borderColor: period === 'AM' ? 'rgba(249, 182, 18, 0.3)' : 'rgba(100, 181, 246, 0.3)',
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': {
              bgcolor: 'rgba(0, 0, 0, 0.4)',
              borderColor: period === 'AM' ? '#F9B612' : '#64b5f6',
            }
          }}
        >
          {period}
        </Box>
      </Box>
    </Box>
  );
};

const BeltSchedulePanel = () => {
  const { configuration, updateConfiguration, station } = usePiers();
  const { activeBelts, beltSchedule, makeupUnits, beltNames, pierSupport, muAssignmentTimeOverride, beltPairs } = configuration;
  const beltGroups = computeBeltGroups(activeBelts, beltPairs);
  const [anchorEl, setAnchorEl] = useState(null);
  const [beltMenuAnchor, setBeltMenuAnchor] = useState(null);
  const [selectedBelt, setSelectedBelt] = useState(null);
  const [copySource, setCopySource] = useState(null);
  const [customPresetDialog, setCustomPresetDialog] = useState(false);
  const [presetName, setPresetName] = useState('');
  const presetKey = `beltCustomPresets_${station}`;
  const [customPresets, setCustomPresets] = useState(() => {
    try {
      const saved = localStorage.getItem(presetKey);
      const parsed = saved ? JSON.parse(saved) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });

  const currentTime = new Date().toTimeString().slice(0, 5);
  
  const isBeltOpen = (beltNumber) => {
    const schedule = beltSchedule[beltNumber];

    // If belt is explicitly closed, it's not open
    if (schedule?.closed) return false;

    if (!schedule || (!schedule.openTime && !schedule.closeTime)) return true;

    const timeToMinutes = (time) => {
      const [hours, minutes] = time.split(':').map(Number);
      return hours * 60 + minutes;
    };

    const currentMinutes = timeToMinutes(currentTime);
    const openMinutes = schedule.openTime ? timeToMinutes(schedule.openTime) : 0;
    const closeMinutes = schedule.closeTime ? timeToMinutes(schedule.closeTime) : 1440;

    if (closeMinutes < openMinutes) {
      return currentMinutes >= openMinutes || currentMinutes < closeMinutes;
    }

    return currentMinutes >= openMinutes && currentMinutes < closeMinutes;
  };
  
  const handleScheduleChange = (beltNumber, field, value) => {
    const newSchedule = {
      ...beltSchedule,
      [beltNumber]: {
        ...(beltSchedule[beltNumber] || { openTime: '03:00', closeTime: '02:59' }),
        [field]: value
      }
    };
    updateConfiguration({ beltSchedule: newSchedule });
  };

  const handleMUChange = (beltNumber, value) => {
    const newMakeupUnits = {
      ...makeupUnits,
      [beltNumber]: value
    };
    updateConfiguration({ makeupUnits: newMakeupUnits });
  };

  const handleBeltNameChange = (beltNumber, value) => {
    const newBeltNames = {
      ...beltNames,
      [beltNumber]: value
    };
    updateConfiguration({ beltNames: newBeltNames });
  };

  const handlePierSupportChange = (muName, value) => {
    const newPierSupport = {
      ...pierSupport,
      [muName]: value
    };
    updateConfiguration({ pierSupport: newPierSupport });
  };

  const handleMUOverrideToggle = (event) => {
    const newOverride = {
      ...muAssignmentTimeOverride,
      enabled: event.target.checked
    };
    updateConfiguration({ muAssignmentTimeOverride: newOverride });
  };

  const handleMUOverrideTimeChange = (field, value) => {
    const newOverride = {
      ...muAssignmentTimeOverride,
      [field]: value
    };
    updateConfiguration({ muAssignmentTimeOverride: newOverride });
  };

  const handlePresetClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  const handlePresetClose = () => {
    setAnchorEl(null);
  };

  const handleBeltMenuOpen = (event, beltNumber) => {
    event.stopPropagation();
    setBeltMenuAnchor(event.currentTarget);
    setSelectedBelt(beltNumber);
  };

  const handleBeltMenuClose = () => {
    setBeltMenuAnchor(null);
    setSelectedBelt(null);
  };

  const handleCopyBeltSchedule = (beltNumber) => {
    setCopySource({
      beltNumber,
      schedule: beltSchedule[beltNumber] || { openTime: '03:00', closeTime: '02:59', closed: false }
    });
    handleBeltMenuClose();
  };

  const handlePasteBeltSchedule = (targetBeltNumber) => {
    if (!copySource) return;

    const newSchedule = {
      ...beltSchedule,
      [targetBeltNumber]: { ...copySource.schedule }
    };
    updateConfiguration({ beltSchedule: newSchedule });
    handleBeltMenuClose();
  };

  const handleClearBeltSchedule = (beltNumber) => {
    const newSchedule = {
      ...beltSchedule,
      [beltNumber]: { openTime: '03:00', closeTime: '02:59', closed: false }
    };
    updateConfiguration({ beltSchedule: newSchedule });
    handleBeltMenuClose();
  };

  const saveCustomPreset = () => {
    if (!presetName.trim()) return;

    const newPreset = {
      id: Date.now(),
      name: presetName,
      schedule: { ...beltSchedule }
    };

    const updatedPresets = [...customPresets, newPreset];
    setCustomPresets(updatedPresets);
    localStorage.setItem(presetKey, JSON.stringify(updatedPresets));
    setPresetName('');
    setCustomPresetDialog(false);
  };

  const applyCustomPreset = (preset) => {
    updateConfiguration({ beltSchedule: preset.schedule });
    handlePresetClose();
  };

  const deleteCustomPreset = (presetId) => {
    const updatedPresets = customPresets.filter(p => p.id !== presetId);
    setCustomPresets(updatedPresets);
    localStorage.setItem(presetKey, JSON.stringify(updatedPresets));
  };

  const applyPreset = (preset) => {
    const newSchedule = {};
    
    switch (preset) {
      case 'allDay':
        // All belts open 24/7
        for (let i = 1; i <= activeBelts; i++) {
          newSchedule[i] = { openTime: '03:00', closeTime: '02:59' };
        }
        break;
      case 'dayShift':
        // All belts open 6am-6pm
        for (let i = 1; i <= activeBelts; i++) {
          newSchedule[i] = { openTime: '06:00', closeTime: '18:00' };
        }
        break;
      case 'nightShift':
        // All belts open 6pm-6am
        for (let i = 1; i <= activeBelts; i++) {
          newSchedule[i] = { openTime: '18:00', closeTime: '06:00' };
        }
        break;
      case 'alternating':
        // Odd belts day shift, even belts night shift
        for (let i = 1; i <= activeBelts; i++) {
          if (i % 2 === 1) {
            newSchedule[i] = { openTime: '06:00', closeTime: '18:00' };
          } else {
            newSchedule[i] = { openTime: '18:00', closeTime: '06:00' };
          }
        }
        break;
      default:
        break;
    }

    updateConfiguration({ beltSchedule: newSchedule });
    handlePresetClose();
  };

  // Initialize schedules if they don't exist
  React.useEffect(() => {
    const newSchedule = { ...beltSchedule };
    let hasChanges = false;
    
    for (let i = 1; i <= activeBelts; i++) {
      if (!newSchedule[i]) {
        newSchedule[i] = { openTime: '03:00', closeTime: '02:59' };
        hasChanges = true;
      }
    }
    
    if (hasChanges) {
      updateConfiguration({ beltSchedule: newSchedule });
    }
  }, [activeBelts, beltSchedule, updateConfiguration]);

  return (
    <Paper
      sx={{
        p: 3,
        bgcolor: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: 2
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h5" sx={{ color: '#C0C0C0', fontWeight: 600 }}>
            Belt Operating Hours
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccessTimeIcon sx={{ fontSize: 16, color: 'rgba(255, 255, 255, 0.5)' }} />
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
              {currentTime}
            </Typography>
          </Box>
        </Box>
        <Button
          size="small"
          startIcon={<ScheduleIcon />}
          onClick={handlePresetClick}
          sx={{
            color: '#F9B612',
            borderColor: 'rgba(255, 255, 255, 0.2)',
            '&:hover': {
              bgcolor: 'rgba(249, 182, 18, 0.1)',
              borderColor: '#F9B612'
            }
          }}
          variant="outlined"
        >
          Presets
        </Button>
      </Box>
      
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handlePresetClose}
        PaperProps={{
          sx: {
            bgcolor: 'rgba(30, 30, 30, 0.95)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            minWidth: 250,
            '& .MuiMenuItem-root': {
              color: '#fff',
              '&:hover': {
                bgcolor: 'rgba(249, 182, 18, 0.1)'
              }
            }
          }
        }}
      >
        <MenuItem disabled sx={{ opacity: '0.5 !important', fontSize: '0.75rem' }}>
          Built-in Presets
        </MenuItem>
        <MenuItem onClick={() => applyPreset('allDay')}>All Belts 24/7</MenuItem>
        <MenuItem onClick={() => applyPreset('dayShift')}>All Day Shift (6am-6pm)</MenuItem>
        <MenuItem onClick={() => applyPreset('nightShift')}>All Night Shift (6pm-6am)</MenuItem>
        <MenuItem onClick={() => applyPreset('alternating')}>Alternating Shifts</MenuItem>

        {customPresets.length > 0 && (
          <>
            <Divider sx={{ my: 1, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
            <MenuItem disabled sx={{ opacity: '0.5 !important', fontSize: '0.75rem' }}>
              Custom Presets
            </MenuItem>
            {customPresets.map((preset) => (
              <MenuItem
                key={preset.id}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  '&:hover .delete-btn': {
                    opacity: 1
                  }
                }}
              >
                <Box onClick={() => applyCustomPreset(preset)} sx={{ flex: 1 }}>
                  {preset.name}
                </Box>
                <IconButton
                  className="delete-btn"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteCustomPreset(preset.id);
                  }}
                  sx={{
                    opacity: 0,
                    transition: 'opacity 0.2s',
                    p: 0.5,
                    color: '#FF5252',
                    '&:hover': {
                      bgcolor: 'rgba(255, 82, 82, 0.1)'
                    }
                  }}
                >
                  <DeleteIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </MenuItem>
            ))}
          </>
        )}

        <Divider sx={{ my: 1, borderColor: 'rgba(255, 255, 255, 0.1)' }} />
        <MenuItem
          onClick={() => {
            setCustomPresetDialog(true);
            handlePresetClose();
          }}
          sx={{
            color: '#F9B612',
            '&:hover': {
              bgcolor: 'rgba(249, 182, 18, 0.15)'
            }
          }}
        >
          <SaveIcon sx={{ fontSize: 18, mr: 1 }} />
          Save Current as Preset
        </MenuItem>
      </Menu>
      
      <Grid container spacing={1.5}>
        {Array.from({ length: activeBelts }, (_, i) => i + 1).map((beltNumber) => {
          const schedule = beltSchedule[beltNumber] || { openTime: '03:00', closeTime: '02:59' };
          const groupInfo = beltGroups[beltNumber];
          const isFirstInPair = groupInfo?.isFirst && groupInfo?.groupSize > 1;
          const isSecondInPair = groupInfo?.isLast && groupInfo?.groupSize > 1;
          
          return (
            <Grid item xs={6} sm={4} md={3} lg={2} key={beltNumber}>
              <Box
                sx={{
                  p: 1.5,
                  bgcolor: 'rgba(255, 255, 255, 0.03)',
                  backdropFilter: 'blur(5px)',
                  WebkitBackdropFilter: 'blur(5px)',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'rgba(255, 255, 255, 0.1)',
                  borderLeft: isFirstInPair ? '3px solid #F9B612' : '1px solid rgba(255, 255, 255, 0.1)',
                  borderRight: isSecondInPair ? '3px solid #F9B612' : 'none',
                  transition: 'all 0.2s',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.05)',
                    transform: 'translateY(-1px)'
                  }
                }}
              >
                <Box
                  onClick={(e) => handleBeltMenuOpen(e, beltNumber)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 1,
                    gap: 0.5,
                    cursor: 'pointer',
                    p: 0.5,
                    mx: -0.5,
                    borderRadius: 0.5,
                    transition: 'all 0.2s',
                    bgcolor: copySource?.beltNumber === beltNumber ? 'rgba(249, 182, 18, 0.05)' : 'transparent',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.03)',
                    }
                  }}
                >
                  <TextField
                    value={beltNames?.[beltNumber] || `Belt ${beltNumber}`}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleBeltNameChange(beltNumber, e.target.value);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    placeholder={`Belt ${beltNumber}`}
                    size="small"
                    sx={{
                      flex: 1,
                      '& .MuiInputBase-root': {
                        bgcolor: 'rgba(0, 0, 0, 0.2)',
                        color: '#fff',
                        borderRadius: 1,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        height: 28,
                        '&:hover': {
                          bgcolor: 'rgba(0, 0, 0, 0.3)',
                        }
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.1)',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.2)',
                      },
                      '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#F9B612',
                      },
                      '& input::placeholder': {
                        color: 'rgba(255, 255, 255, 0.3)',
                        opacity: 1
                      }
                    }}
                  />

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {copySource?.beltNumber === beltNumber && (
                      <Box sx={{
                        px: 0.75,
                        py: 0.25,
                        bgcolor: 'rgba(249, 182, 18, 0.15)',
                        borderRadius: 0.5,
                        border: '1px solid rgba(249, 182, 18, 0.3)',
                      }}>
                        <Typography sx={{ fontSize: '0.65rem', color: '#F9B612', fontWeight: 600 }}>
                          COPIED
                        </Typography>
                      </Box>
                    )}

                    <MoreVertIcon sx={{
                      fontSize: 16,
                      color: 'rgba(255, 255, 255, 0.4)',
                      transition: 'color 0.2s'
                    }} />

                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: schedule.closed ? '#FF5252' : '#4CAF50',
                        boxShadow: schedule.closed
                          ? '0 0 8px rgba(255, 82, 82, 0.6)'
                          : '0 0 8px rgba(76, 175, 80, 0.6)',
                      }}
                    />
                  </Box>
                </Box>
                
                <Box sx={{ display: 'flex', gap: 0.75, flexDirection: 'column' }}>
                  {/* Closed Toggle - Custom Design */}
                  <Box
                    onClick={() => handleScheduleChange(beltNumber, 'closed', !schedule.closed)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      cursor: 'pointer',
                      p: 0.75,
                      borderRadius: 1,
                      bgcolor: schedule.closed ? 'rgba(255, 82, 82, 0.15)' : 'rgba(0, 0, 0, 0.2)',
                      border: '1px solid',
                      borderColor: schedule.closed ? 'rgba(255, 82, 82, 0.4)' : 'rgba(255, 255, 255, 0.1)',
                      transition: 'all 0.2s',
                      mb: 0.75,
                      '&:hover': {
                        bgcolor: schedule.closed ? 'rgba(255, 82, 82, 0.25)' : 'rgba(0, 0, 0, 0.3)',
                        borderColor: schedule.closed ? '#FF5252' : 'rgba(255, 255, 255, 0.2)',
                        transform: 'translateY(-1px)'
                      }
                    }}
                  >
                    {/* Custom Toggle Switch */}
                    <Box
                      sx={{
                        width: 36,
                        height: 20,
                        borderRadius: 10,
                        bgcolor: schedule.closed ? '#FF5252' : 'rgba(255, 255, 255, 0.1)',
                        position: 'relative',
                        transition: 'all 0.2s',
                        border: '1px solid',
                        borderColor: schedule.closed ? '#FF5252' : 'rgba(255, 255, 255, 0.2)',
                      }}
                    >
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          borderRadius: '50%',
                          bgcolor: schedule.closed ? '#fff' : 'rgba(255, 255, 255, 0.6)',
                          position: 'absolute',
                          top: '50%',
                          transform: schedule.closed ? 'translate(18px, -50%)' : 'translate(2px, -50%)',
                          transition: 'all 0.2s',
                          boxShadow: schedule.closed
                            ? '0 2px 4px rgba(0,0,0,0.2)'
                            : '0 1px 2px rgba(0,0,0,0.1)'
                        }}
                      />
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: schedule.closed ? '#FF5252' : 'rgba(255, 255, 255, 0.6)',
                        fontWeight: schedule.closed ? 600 : 400,
                        fontSize: '0.7rem',
                        userSelect: 'none'
                      }}
                    >
                      {schedule.closed ? 'Belt Closed' : 'Belt Open'}
                    </Typography>
                  </Box>

                  {!schedule.closed && (
                    <>
                      <TimePicker
                        label="Open:"
                        value={schedule.openTime || '03:00'}
                        onChange={(newTime) => handleScheduleChange(beltNumber, 'openTime', newTime)}
                      />

                      <TimePicker
                        label="Close:"
                        value={schedule.closeTime || '02:59'}
                        onChange={(newTime) => handleScheduleChange(beltNumber, 'closeTime', newTime)}
                      />
                    </>
                  )}

                  {schedule.closed && (
                    <Box sx={{
                      p: 1,
                      bgcolor: 'rgba(255, 82, 82, 0.1)',
                      borderRadius: 1,
                      border: '1px solid rgba(255, 82, 82, 0.3)',
                      textAlign: 'center'
                    }}>
                      <Typography variant="caption" sx={{ color: '#FF5252', fontWeight: 600 }}>
                        No Flights Accepted
                      </Typography>
                    </Box>
                  )}

                  <Box>
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.65rem', mb: 0.25, display: 'block' }}>
                      MU:
                    </Typography>
                    <TextField
                      value={makeupUnits?.[beltNumber] || ''}
                      onChange={(e) => handleMUChange(beltNumber, e.target.value)}
                      placeholder="MU#"
                      fullWidth
                      size="small"
                      disabled={schedule.closed}
                      sx={{
                        '& .MuiInputBase-root': {
                          bgcolor: 'rgba(0, 0, 0, 0.2)',
                          color: '#fff',
                          borderRadius: 1,
                          fontSize: '0.8rem',
                          height: 32,
                          '&:hover': {
                            bgcolor: 'rgba(0, 0, 0, 0.3)',
                          },
                          '&.Mui-disabled': {
                            bgcolor: 'rgba(0, 0, 0, 0.1)',
                            color: 'rgba(255, 255, 255, 0.3)',
                          }
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(255, 255, 255, 0.1)',
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: 'rgba(255, 255, 255, 0.2)',
                        },
                        '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#F9B612',
                        },
                        '& input::placeholder': {
                          color: 'rgba(255, 255, 255, 0.3)',
                          opacity: 1
                        }
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </Grid>
          );
        })}
      </Grid>
      
      <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 3 }}>
        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
          <Box component="span" sx={{ color: '#4CAF50', fontWeight: 600, fontSize: '1rem' }}>●</Box> Open belts accept flights
        </Typography>
        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
          <Box component="span" sx={{ color: '#FF5252', fontWeight: 600, fontSize: '1rem' }}>●</Box> Closed belts reject flights
        </Typography>
      </Box>

      {/* MU Assignment Time Override Section */}
      <Divider sx={{ my: 3, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <AssignmentIcon sx={{ fontSize: 20, color: '#F9B612' }} />
          <Typography variant="h6" sx={{ color: '#C0C0C0', fontWeight: 600 }}>
            MU Assignment Sheet Display Times
          </Typography>
        </Box>

        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', mb: 2, display: 'block' }}>
          Override the open/close times shown on the MU Assignments sheet (for vendor display purposes)
        </Typography>

        <FormControlLabel
          control={
            <Switch
              checked={muAssignmentTimeOverride?.enabled || false}
              onChange={handleMUOverrideToggle}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: '#F9B612',
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: '#F9B612',
                }
              }}
            />
          }
          label={
            <Typography variant="body2" sx={{ color: '#fff' }}>
              Override belt times on MU Assignments sheet
            </Typography>
          }
          sx={{ mb: 2 }}
        />

        {muAssignmentTimeOverride?.enabled && (
          <Box
            sx={{
              p: 2,
              bgcolor: 'rgba(249, 182, 18, 0.05)',
              borderRadius: 1,
              border: '1px solid rgba(249, 182, 18, 0.2)'
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <TimePicker
                  label="Display Open Time:"
                  value={muAssignmentTimeOverride?.openTime || '01:30'}
                  onChange={(newTime) => handleMUOverrideTimeChange('openTime', newTime)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TimePicker
                  label="Display Close Time:"
                  value={muAssignmentTimeOverride?.closeTime || '01:25'}
                  onChange={(newTime) => handleMUOverrideTimeChange('closeTime', newTime)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)', fontStyle: 'italic' }}>
                  These times will appear on the MU Assignments sheet instead of individual belt schedules.
                  Actual belt operations still use the times configured above.
                </Typography>
              </Grid>
            </Grid>
          </Box>
        )}
      </Box>

      {/* Pier Support Section */}
      <Divider sx={{ my: 3, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <LocalOfferIcon sx={{ fontSize: 20, color: '#F9B612' }} />
          <Typography variant="h6" sx={{ color: '#C0C0C0', fontWeight: 600 }}>
            Pier Support Tag Numbers
          </Typography>
        </Box>

        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)', mb: 2, display: 'block' }}>
          Bag tag numbers used by the city to identify belt routing
        </Typography>

        <Grid container spacing={2}>
          {/* Get unique MU values from the configuration */}
          {Array.from(new Set(Object.values(makeupUnits || {})))
            .filter(Boolean)
            .sort()
            .map((muName) => (
              <Grid item xs={6} sm={4} md={3} lg={2} key={muName}>
                <Box
                  sx={{
                    p: 1.5,
                    bgcolor: 'rgba(255, 255, 255, 0.03)',
                    borderRadius: 1,
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.05)',
                      borderColor: 'rgba(249, 182, 18, 0.3)'
                    }
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#F9B612',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      mb: 0.5,
                      display: 'block'
                    }}
                  >
                    {muName}
                  </Typography>
                  <TextField
                    value={pierSupport?.[muName] || ''}
                    onChange={(e) => handlePierSupportChange(muName, e.target.value)}
                    placeholder="Tag #"
                    fullWidth
                    size="small"
                    sx={{
                      '& .MuiInputBase-root': {
                        bgcolor: 'rgba(0, 0, 0, 0.2)',
                        color: '#fff',
                        borderRadius: 1,
                        fontSize: '0.9rem',
                        fontWeight: 600,
                        height: 32,
                        '&:hover': {
                          bgcolor: 'rgba(0, 0, 0, 0.3)',
                        }
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.1)',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(255, 255, 255, 0.2)',
                      },
                      '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#F9B612',
                      },
                      '& input::placeholder': {
                        color: 'rgba(255, 255, 255, 0.3)',
                        opacity: 1
                      },
                      '& input': {
                        textAlign: 'center'
                      }
                    }}
                  />
                </Box>
              </Grid>
            ))}
        </Grid>
      </Box>
      {/* Belt Actions Menu */}
      <Menu
        anchorEl={beltMenuAnchor}
        open={Boolean(beltMenuAnchor)}
        onClose={handleBeltMenuClose}
        PaperProps={{
          sx: {
            bgcolor: 'rgba(30, 30, 30, 0.95)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            minWidth: 180,
            '& .MuiMenuItem-root': {
              color: '#fff',
              fontSize: '0.875rem',
              '&:hover': {
                bgcolor: 'rgba(249, 182, 18, 0.1)'
              }
            }
          }
        }}
      >
        <MenuItem
          onClick={() => selectedBelt && handleCopyBeltSchedule(selectedBelt)}
          sx={{
            bgcolor: copySource?.beltNumber === selectedBelt ? 'rgba(249, 182, 18, 0.1)' : 'transparent',
          }}
        >
          <ListItemIcon sx={{ minWidth: 36 }}>
            <ContentCopyIcon sx={{ fontSize: 18, color: copySource?.beltNumber === selectedBelt ? '#F9B612' : 'rgba(255, 255, 255, 0.7)' }} />
          </ListItemIcon>
          <ListItemText
            primary="Copy Schedule"
            secondary={copySource?.beltNumber === selectedBelt && "(Copied)"}
            secondaryTypographyProps={{ sx: { color: '#F9B612', fontSize: '0.7rem' } }}
          />
        </MenuItem>

        {copySource && copySource.beltNumber !== selectedBelt && (
          <MenuItem onClick={() => selectedBelt && handlePasteBeltSchedule(selectedBelt)}>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <ContentPasteIcon sx={{ fontSize: 18, color: '#4CAF50' }} />
            </ListItemIcon>
            <ListItemText
              primary="Paste Schedule"
              secondary={`From ${beltNames?.[copySource.beltNumber] || `Belt ${copySource.beltNumber}`}`}
              secondaryTypographyProps={{ sx: { color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.7rem' } }}
            />
          </MenuItem>
        )}

        <Divider sx={{ my: 0.5, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

        <MenuItem onClick={() => selectedBelt && handleClearBeltSchedule(selectedBelt)}>
          <ListItemIcon sx={{ minWidth: 36 }}>
            <ClearIcon sx={{ fontSize: 18, color: 'rgba(255, 255, 255, 0.7)' }} />
          </ListItemIcon>
          <ListItemText primary="Reset to Default" />
        </MenuItem>
      </Menu>

      {/* Custom Preset Dialog */}
      <Dialog
        open={customPresetDialog}
        onClose={() => setCustomPresetDialog(false)}
        PaperProps={{
          sx: {
            bgcolor: 'rgba(30, 30, 30, 0.95)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            minWidth: 400
          }
        }}
      >
        <DialogTitle sx={{ color: '#F9B612', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
          Save Schedule Preset
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <TextField
            autoFocus
            label="Preset Name"
            fullWidth
            value={presetName}
            onChange={(e) => setPresetName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                saveCustomPreset();
              }
            }}
            placeholder="e.g., Weekend Schedule, Holiday Hours"
            sx={{
              '& .MuiInputBase-root': {
                color: '#fff',
              },
              '& .MuiInputLabel-root': {
                color: 'rgba(255, 255, 255, 0.5)',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 255, 255, 0.2)',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgba(255, 255, 255, 0.3)',
              },
              '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#F9B612',
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: '#F9B612',
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)', p: 2 }}>
          <Button
            onClick={() => {
              setCustomPresetDialog(false);
              setPresetName('');
            }}
            sx={{ color: 'rgba(255, 255, 255, 0.5)' }}
          >
            Cancel
          </Button>
          <Button
            onClick={saveCustomPreset}
            disabled={!presetName.trim()}
            variant="contained"
            sx={{
              bgcolor: '#F9B612',
              color: '#000',
              '&:hover': {
                bgcolor: '#E0A510',
              },
              '&.Mui-disabled': {
                bgcolor: 'rgba(249, 182, 18, 0.3)',
                color: 'rgba(0, 0, 0, 0.3)'
              }
            }}
          >
            Save Preset
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default BeltSchedulePanel;